import { Formik, FormikValues } from 'formik';
import { useCallback, useRef, useState } from 'react';
import { useAlert } from 'components';

import { ErrorLabel, InputField } from 'common';
import { Icon } from 'common/icon';
import { IUser } from 'constants/types';
import { useClickOutside } from 'hooks/useClickOutside';
import { handleMutation } from 'utils/handleMutation';
import { ModalMain } from '../main';
import { initialValues, model, validationSchema } from './config';

import { useUserUpdateUsernameMutation } from 'hooks/api/users';

import styles from './styles.module.css';
import { Button } from 'common/new-button';
import { Analytics } from 'constants/analytics';

interface Props {
  data?: IUser;
  isVisible: boolean;
  onClose?: () => void;
}

const varinatError = {
  duplicatekey: 'duplicatekey',
};

export const EditUsernameModal = ({ data, isVisible, onClose }: Props) => {
  const alert = useAlert();
  const modalRef = useRef<HTMLDivElement | null>(null);

  useClickOutside({ onClick: onClose, ref: modalRef });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [updateUsername, updateUsernameMutation] =
    useUserUpdateUsernameMutation();

  const isLoadingUsername = updateUsernameMutation.isLoading;

  const onSuccessUsername = useCallback(() => {
    alert.success('Update username successfully added');
  }, [alert]);

  const onErrorUsername = useCallback(
    (error: any) => {
      if (error?.data?.error?.codeName) {
        const codeName = error?.data?.error?.codeName?.toLowerCase();
        const constantValue = varinatError.duplicatekey;
        if (codeName === constantValue) {
          setErrorMessage('this username already taken');
        }
      } else {
        alert.error(error?.data?.error || 'Error');
      }
    },
    [alert]
  );

  handleMutation({
    ...updateUsernameMutation,
    onSuccess: onSuccessUsername,
    onError: onErrorUsername,
  });

  const onSubmit = async (values: FormikValues) => {
    const { username } = values;

    try {
      const res: any = await updateUsername({ username });

      if (res) {
        if (onClose) {
          onClose();
        }
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      alert.error('Unexpected error occurred');
    }
  };

  return (
    <ModalMain
      width={260}
      title=""
      targetRef={modalRef}
      isVisible={isVisible}
      closeOnClick={onClose}
      footer=""
    >
      <Formik
        onSubmit={onSubmit}
        initialValues={initialValues(data)}
        validationSchema={() => validationSchema()}
      >
        {({ dirty, isSubmitting, errors, submitForm }) => (
          <div className={styles.body}>
            <div className={styles.wrapper_header}>
              <span>{'Edit username'}</span>
              <div className={styles.iconClose} onClick={onClose}>
                <Icon.Close />
              </div>
            </div>
            <div className={styles.wrapper_content}>
              <div className={styles.wrapper_input}>
                <InputField {...model.username} variant="editProfile" />
                {errors?.username && (
                  <ErrorLabel text={errors?.username?.toString()} />
                )}
                {errorMessage && <ErrorLabel text={errorMessage} />}
              </div>

              <div className={styles.submitButton}>
                <Button
                  id={Analytics.user.editUsername}
                  onClick={submitForm}
                  disabled={
                    !!errors?.username?.length || !dirty || isSubmitting
                  }
                  text={isLoadingUsername ? 'Loading...' : 'Submit'}
                />
              </div>
              <div className={styles.cancelButton}>
                <Button text="Cancel" onClick={onClose} />
              </div>
            </div>
          </div>
        )}
      </Formik>
    </ModalMain>
  );
};
